import { useEffect, useMemo } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import useGet from "../../common/useGet";
import {
  API_SETTLEMENT_BATCHES,
  API_SETTLEMENT_BENEFICIARIES,
  API_SETTLEMENT_BATCHES_SUBMIT,
  API_SETTLEMENT_BATCHES_DECIDE,
  API_SETTLEMENT_BATCHES_EXECUTE,
  API_SETTLEMENT_BATCHES_RETRY,
} from "../../../utils/apis";
import {
  APPROVED,
  APPROVE,
  BENEFICIARY_TYPES,
  DRAFT,
  EXECUTE,
  FAILED,
  PENDING_APPROVAL,
  REJECT,
  REJECTED,
  RETRY,
  SEND_TO_FM,
  SENT_TO_BANK,
  SETTLED,
  SUBMIT,
} from "../../../utils/constants";
import { IFormSection } from "../../../types/common";
import {
  createColumn,
  createViewAction,
  formatCurrency,
  formatDateTime,
  getForm,
  getRegister,
} from "../../../utils/helpers";
import { message } from "antd";
import { Table } from "../../../components/common/Table";
import { URL_SETTLEMENT_BATCHES } from "../../../utils/urls";
import { FormActions } from "../../../components/common/FormActions";
import {
  BooleanTags,
  TransactionStatusTag,
} from "../../../components/common/registerUtils";
import useSet from "../../common/useSet";

const url = URL_SETTLEMENT_BATCHES;

export const useSettlementBatches = () => {
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const { error, success, uploadData } = useSet();

  const status = useMemo(() => {
    return location.state?.filterParams?.status || searchParams.get("status");
  }, [location.state, searchParams]);

  const getColumnsByStatus = (statusType: string) => {
    switch (statusType) {
      case DRAFT:
        return [
          createColumn("batch_reference", true, "Batch Reference"),
          createColumn("beneficiary", true, "Beneficiary"),
          createColumn("total_amount", true, "Total Amount"),
          createColumn("transaction_count", true, "Transaction Count"),
          createColumn("created_at", true, "Created At"),
          createColumn("prepared_by", true, "Prepared By"),
          createColumn("actions", false),
        ];
      case PENDING_APPROVAL:
        return [
          createColumn("batch_reference", true, "Batch Reference"),
          createColumn("beneficiary", true, "Beneficiary"),
          createColumn("total_amount", true, "Total Amount"),
          createColumn("transaction_count", true, "Transaction Count"),
          createColumn(
            "current_approval_level",
            false,
            "Current Approval Level",
          ),
          createColumn("requires_fm_approval", false, "Requires F.M Approval"),
          createColumn("created_at", true, "Created At"),
          createColumn("prepared_by", true, "Prepared By"),
          createColumn("actions", false),
        ];
      case APPROVED:
        return [
          createColumn("batch_reference", true, "Batch Reference"),
          createColumn("beneficiary", true, "Beneficiary"),
          createColumn("total_amount", true, "Total Amount"),
          createColumn("transaction_count", true, "Transaction Count"),
          createColumn("created_at", true, "Created At"),
          createColumn("prepared_by", true, "Prepared By"),
          createColumn("actions", false),
        ];
      // case REJECTED:
      //   return [
      //     createColumn("batch_reference", true, "Batch Reference"),
      //     createColumn("beneficiary", true, "Beneficiary"),
      //     createColumn("total_amount", true, "Total Amount"),
      //     createColumn("transaction_count", true, "Transaction Count"),
      //     createColumn("created_at", true, "Created At"),
      //     createColumn("prepared_by", true, "Prepared By"),
      //     createColumn("actions", false),
      //   ];
      case SENT_TO_BANK:
        return [
          createColumn("batch_reference", true, "Batch Reference"),
          createColumn("beneficiary", true, "Beneficiary"),
          createColumn("total_amount", true, "Total Amount"),
          createColumn("transaction_count", true, "Transaction Count"),
          createColumn("created_at", true, "Created At"),
          createColumn("prepared_by", true, "Prepared By"),
          createColumn("actions", false),
        ];
      case SETTLED:
        return [
          createColumn("batch_reference", true, "Batch Reference"),
          createColumn("beneficiary", true, "Beneficiary"),
          createColumn("total_amount", true, "Total Amount"),
          createColumn("transaction_count", true, "Transaction Count"),
          createColumn("created_at", true, "Created At"),
          createColumn("prepared_by", true, "Prepared By"),
          createColumn("actions", false),
        ];
      case FAILED:
        return [
          createColumn("batch_reference", true, "Batch Reference"),
          createColumn("beneficiary", true, "Beneficiary"),
          createColumn("total_amount", true, "Total Amount"),
          createColumn("transaction_count", true, "Transaction Count"),
          createColumn("created_at", true, "Created At"),
          createColumn("prepared_by", true, "Prepared By"),
          createColumn("actions", false),
        ];
      default:
        return [
          createColumn("batch_reference", true, "Batch Reference"),
          createColumn("beneficiary", true, "Beneficiary"),
          createColumn("total_amount", true, "Total Amount"),
          createColumn("transaction_count", true, "Transaction Count"),
          createColumn("status", true, "Status"),
          createColumn("created_at", true, "Created At"),
          createColumn("prepared_by", true, "Prepared By"),
          createColumn("actions", false),
        ];
    }
  };

  const tableColumns = () => [...getColumnsByStatus(status)];

  const getBatchActions = (item: any) => {
    const baseActions = [createViewAction(url, item?.id)];

    switch (item?.status?.toLowerCase()) {
      case DRAFT:
        return [
          ...baseActions,
          {
            label: SUBMIT,
            custom: true,
            handleCustom: () =>
              uploadData({
                api: API_SETTLEMENT_BATCHES_SUBMIT,
                method: "POST",
                params: { batch_id: item?.id },
              }),
          },
        ];
      case PENDING_APPROVAL: {
        const currentLevel = item?.current_approval_level;
        const requiresFm = item?.requires_fm_approval;

        const approveLabel =
          currentLevel === 2 && requiresFm ? SEND_TO_FM : APPROVE;

        return [
          ...baseActions,
          {
            label: approveLabel,
            custom: true,
            showReasonField: true,
            modalTitle: `Confirm ${approveLabel}`,
            modalOkType: "primary" as const,
            handleCustom: (comments?: string) =>
              uploadData({
                api: API_SETTLEMENT_BATCHES_DECIDE,
                method: "POST",
                params: {
                  batch_id: item?.id,
                  approval_level: item.current_approval_level,
                  decision: APPROVED.toUpperCase(),
                  comments,
                },
              }),
          },
          {
            label: REJECT,
            custom: true,
            showReasonField: true,
            modalTitle: "Confirm Reject",
            modalOkType: "danger" as const,
            handleCustom: (comments?: string) =>
              uploadData({
                api: API_SETTLEMENT_BATCHES_DECIDE,
                method: "POST",
                params: {
                  batch_id: item?.id,
                  approval_level: item?.current_approval_level,
                  decision: REJECTED.toUpperCase(),
                  comments,
                },
              }),
          },
        ];
      }
      case APPROVED:
        return [
          ...baseActions,
          {
            label: EXECUTE,
            custom: true,
            handleCustom: () =>
              uploadData({
                api: API_SETTLEMENT_BATCHES_EXECUTE,
                method: "POST",
                params: { batch_id: item?.id },
              }),
          },
        ];
      case FAILED:
        return [
          ...baseActions,
          {
            label: RETRY,
            custom: true,
            handleCustom: () =>
              uploadData({
                api: API_SETTLEMENT_BATCHES_RETRY,
                method: "POST",
                params: { batch_id: item?.id },
              }),
          },
        ];
      default:
        return baseActions;
    }
  };

  useEffect(() => {
    if (success) {
      message.success(`Updated transaction reversal status`);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }

    if (Object?.keys(error)?.length > 0) {
      message.error(
        error?.detail || "Failed to update transaction reversal status",
      );
    }
  }, [success, error]);

  const tableData = (data: any[] = []) => {
    if (!Array.isArray(data)) return [];
    return data.map((item) => {
      return {
        ...item,
        total_amount: formatCurrency(item?.total_amount),
        created_at: formatDateTime(item?.created_at),
        submitted_at: formatDateTime(item?.submitted_at),
        approved_at: formatDateTime(item?.approved_at),
        transaction_count: item?.transaction_count || "0",
        beneficiary: item?.beneficiary.name,
        requires_fm_approval: (
          <BooleanTags status={item?.requires_fm_approval} />
        ),
        status: <TransactionStatusTag status={item?.status} />,
        prepared_by: item?.prepared_by.name,
        actions: <FormActions actions={getBatchActions(item)} />,
      };
    });
  };

  const viewData = (item: any) => {
    return {
      sections: [
        {
          title: "Batch Details",
          fields: {
            batch_reference: item?.batch_reference,
            total_amount: formatCurrency(item?.total_amount),
            transaction_count: item?.transaction_count,
            status: <TransactionStatusTag status={item?.status} />,
            settlement_date: item?.settlement_date,
            retry_count: item?.retry_count || "0",
            requires_fm_approval: (
              <BooleanTags status={item?.requires_fm_approval} />
            ),
            risk_flags: [],
            is_locked: <BooleanTags status={item?.is_locked} />,
            created_at: formatDateTime(item?.created_at),
          },
        },
        {
          title: "Beneficiary",
          fields: {
            beneficiary_type: item?.beneficiary?.beneficiary_type,
            code: item?.beneficiary?.code,
            name: item?.beneficiary?.name,
            bank_name: item?.beneficiary?.bank_name,
            account_number: item?.beneficiary?.account_number,
            account_name: item?.beneficiary?.account_name,
            // is_active: <BooleanTags status={item?.beneficiary?.is_active} />,
          },
        },
        {
          component: (
            <Table
              loading={false}
              tableTitle="Payments"
              count={item?.items?.length || 0}
              tableColumns={[
                {
                  id: "transaction_id",
                  header: "Transaction ID",
                  render: (row: any) => row.transaction_id,
                },
                {
                  id: "amount",
                  header: "Amount",
                  render: (row: any) => formatCurrency(parseFloat(row.amount)),
                },
                {
                  id: "final_status",
                  header: "Final Status",
                  render: (row: any) => (
                    <TransactionStatusTag
                      status={row.eligibility_snapshot?.final_status}
                    />
                  ),
                },
                {
                  id: "checked_at",
                  header: "Checked At",
                  render: (row: any) =>
                    row.eligibility_snapshot?.checked_at
                      ? formatDateTime(row.eligibility_snapshot.checked_at)
                      : "N/A",
                },
              ]}
              tableData={item?.items || []}
              hideFilters={false}
              showPagination={false}
            />
          ),
        },
      ],
    };
  };

  const formRoute = status === DRAFT ? getForm(url) : null;

  const createTitle = "Build Batch";

  const endpoint = `settlement/batches/${id}/audit-trail/`;

  return {
    tableColumns,
    tableData,
    viewData,
    formRoute,
    createTitle,
    endpoint,
  };
};

export const useSettlementBatchForm = () => {
  const {
    data: beneficiaries,
    loading: beneficiariesLoading,
    error: beneficiariesError,
    loadData: loadBeneficiaries,
  } = useGet();

  useEffect(() => {
    loadBeneficiaries({ api: API_SETTLEMENT_BENEFICIARIES });
  }, []);

  const sections: IFormSection[] = [
    {
      title: "Build Batch",
      fields: [
        {
          field_name: "beneficiary_code",
          label: "Beneficiary Code",
          type: "select",
          options_data: beneficiaries?.results?.map((item: any) => ({
            id: item.code,
            name: item.name,
          })),
          loading: beneficiariesLoading,
          props: { required: true, placeholder: "Select Beneficiary" },
        },
        {
          field_name: "batch_type",
          label: "Batch Type",
          type: "select",
          options_data: BENEFICIARY_TYPES.map((item: any) => ({
            id: item.id,
            name: item.name,
          })),
          props: { required: true, placeholder: "Select Batch Type" },
        },
        {
          field_name: "currency",
          label: "Currency",
          type: "text",
          visible: false,
          props: { required: true },
        },
        {
          field_name: "payment_date_from",
          label: "Payment Date From",
          type: "date",
          props: { required: true },
        },
        {
          field_name: "payment_date_to",
          label: "Payment Date To",
          type: "date",
          props: { required: true },
        },
      ],
    },
  ];

  const redirect = getRegister(`${url}/${DRAFT}?status=${DRAFT}`);

  function onSubmit(payload: any, id: string | number) {
    const cleanedPayload: any = {};

    Object.keys(payload).forEach((key) => {
      if (!key.endsWith("_name")) {
        cleanedPayload[key] = payload[key];
      }
    });

    if (cleanedPayload.payment_date_from) {
      cleanedPayload.payment_date_from = `${cleanedPayload.payment_date_from}T00:00:00Z`;
    }

    if (cleanedPayload.payment_date_to) {
      cleanedPayload.payment_date_to = `${cleanedPayload.payment_date_to}T23:59:59Z`;
    }

    return {
      id,
      api: `${API_SETTLEMENT_BATCHES}build_batch/`,
      params: cleanedPayload,
    };
  }

  return {
    sections,
    beneficiariesLoading,
    beneficiariesError,
    redirect,
    onSubmit,
    defaultValues: { currency: "UGX" },
  };
};
